import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../utils/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Copy, Share2, Users, Gift, Trophy, CheckCircle, Clock, QrCode } from 'lucide-react';

const SITE_URL = window.location.origin;

export default function ReferralPage() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, listRes] = await Promise.all([
        api.getReferralStats(),
        api.getReferralList(),
      ]);
      setStats(statsRes.data);
      setReferrals(listRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = stats?.code ? `${SITE_URL}/register?ref=${stats.code}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success(t('ref_link_copied'));
  };

  const copyCode = () => {
    navigator.clipboard.writeText(stats?.code || '');
    toast.success(t('ref_code_copied'));
  };

  const shareVia = (platform) => {
    const msg = t('ref_share_message').replace('{link}', referralLink);
    const encoded = encodeURIComponent(msg);
    const urls = {
      line: `https://line.me/R/msg/text/?${encoded}`,
      whatsapp: `https://wa.me/?text=${encoded}`,
      email: `mailto:?subject=${encodeURIComponent(t('ref_share_subject'))}&body=${encoded}`,
    };
    window.open(urls[platform], '_blank');
  };

  const currentTier = stats?.tier;
  const tiers = stats?.tiers || [];

  const getTierColor = (name) => {
    if (name === 'gold') return 'text-yellow-500';
    if (name === 'silver') return 'text-slate-400';
    return 'text-amber-700';
  };

  const getTierBg = (name) => {
    if (name === 'gold') return 'bg-yellow-500/10 border-yellow-500/30';
    if (name === 'silver') return 'bg-slate-400/10 border-slate-400/30';
    return 'bg-amber-700/10 border-amber-700/30';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-accent border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl" data-testid="referral-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">{t('ref_title')}</h1>
          <p className="text-muted-foreground">{t('ref_subtitle')}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border/50 rounded-xl p-4 text-center" data-testid="ref-stat-total">
            <Users className="h-5 w-5 mx-auto mb-2 text-accent" />
            <div className="text-2xl font-bold font-mono text-primary">{stats?.total_referrals || 0}</div>
            <div className="text-xs text-muted-foreground">{t('ref_total')}</div>
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4 text-center" data-testid="ref-stat-completed">
            <CheckCircle className="h-5 w-5 mx-auto mb-2 text-emerald-500" />
            <div className="text-2xl font-bold font-mono text-primary">{stats?.completed_referrals || 0}</div>
            <div className="text-xs text-muted-foreground">{t('ref_completed')}</div>
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4 text-center" data-testid="ref-stat-pending">
            <Clock className="h-5 w-5 mx-auto mb-2 text-amber-500" />
            <div className="text-2xl font-bold font-mono text-primary">{stats?.pending_referrals || 0}</div>
            <div className="text-xs text-muted-foreground">{t('ref_pending')}</div>
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4 text-center" data-testid="ref-stat-free">
            <Gift className="h-5 w-5 mx-auto mb-2 text-violet-500" />
            <div className="text-2xl font-bold font-mono text-primary">{stats?.free_transfers || 0}</div>
            <div className="text-xs text-muted-foreground">{t('ref_free_transfers')}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Referral Code & Share */}
          <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="ref-code-card">
            <h2 className="font-heading text-lg font-semibold text-primary mb-4">{t('ref_your_code')}</h2>
            <div className="bg-secondary/50 rounded-xl p-4 flex items-center justify-between mb-4">
              <span className="text-2xl font-bold font-mono tracking-wider text-accent" data-testid="ref-code-value">
                {stats?.code || '---'}
              </span>
              <Button variant="ghost" size="sm" onClick={copyCode} data-testid="copy-code-btn">
                <Copy className="h-4 w-4" />
              </Button>
            </div>

            <div className="bg-secondary/30 rounded-xl p-3 flex items-center gap-2 mb-4">
              <input
                type="text"
                readOnly
                value={referralLink}
                className="flex-1 bg-transparent text-xs font-mono text-muted-foreground truncate border-none outline-none"
                data-testid="ref-link-input"
              />
              <Button variant="ghost" size="sm" onClick={copyLink} data-testid="copy-link-btn">
                <Copy className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => shareVia('line')} data-testid="share-line-btn">
                LINE
              </Button>
              <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => shareVia('whatsapp')} data-testid="share-whatsapp-btn">
                WhatsApp
              </Button>
              <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => shareVia('email')} data-testid="share-email-btn">
                <Share2 className="h-3 w-3" /> {t('ref_email')}
              </Button>
            </div>
          </div>

          {/* Tier Progress */}
          {stats?.tier_enabled && (
            <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="ref-tier-card">
              <h2 className="font-heading text-lg font-semibold text-primary mb-4">
                <Trophy className="h-5 w-5 inline mr-2 text-yellow-500" />
                {t('ref_tier_title')}
              </h2>

              {currentTier && (
                <div className={`rounded-xl p-4 mb-4 border ${getTierBg(currentTier.name)}`} data-testid="current-tier">
                  <div className="text-sm text-muted-foreground">{t('ref_current_tier')}</div>
                  <div className={`text-xl font-bold ${getTierColor(currentTier.name)}`}>
                    {currentTier.label}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t('ref_fee_discount')}: {currentTier.fee_discount_percent}%
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {tiers.map((tier) => {
                  const completed = stats?.completed_referrals || 0;
                  const progress = Math.min(100, (completed / tier.min_referrals) * 100);
                  const isActive = currentTier?.name === tier.name;
                  return (
                    <div key={tier.name} className={`rounded-lg p-3 border ${isActive ? getTierBg(tier.name) : 'border-border/50'}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className={`font-semibold ${isActive ? getTierColor(tier.name) : 'text-primary'}`}>
                          {tier.label}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {completed}/{tier.min_referrals} {t('ref_people')} ({t('ref_fee_discount')} {tier.fee_discount_percent}%)
                        </span>
                      </div>
                      <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${isActive ? 'bg-accent' : 'bg-muted-foreground/30'}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Referral List */}
        <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="ref-list">
          <h2 className="font-heading text-lg font-semibold text-primary mb-4">{t('ref_history')}</h2>
          {referrals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t('ref_no_referrals')}</div>
          ) : (
            <div className="space-y-3">
              {referrals.map((ref) => (
                <div key={ref.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                  <div>
                    <div className="font-medium text-primary text-sm">{ref.referee_name || ref.referee_email}</div>
                    <div className="text-xs text-muted-foreground">
                      {ref.created_at ? new Date(ref.created_at).toLocaleDateString('ja-JP') : ''}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    ref.status === 'completed'
                      ? 'bg-emerald-500/10 text-emerald-600'
                      : 'bg-amber-500/10 text-amber-600'
                  }`} data-testid={`ref-status-${ref.id}`}>
                    {ref.status === 'completed' ? t('ref_status_completed') : t('ref_status_pending')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
